/**
 * API composable para operaciones de archivos
 * Incluye endpoints para CRUD, subida temporal y obtención de presigned URLs
 */

import { useApiResource } from '~/composables/api/useApiResource'

export const useFilesApi = () => {
  const { list, getById, update, delete: deleteFile, customEndpoint } = useApiResource('files', {
    deserialize: true,
    methods: {
      list: true,
      getById: true,
      create: false,
      update: true,
      delete: true
    }
  })

  /**
   * POST /files/request-upload
   * Solicita URL presigned para subir un archivo con registro inmediato en BD
   * @param {Object} data - Datos del archivo a subir
   * @param {string|number} data.promotionId - ID de la promoción
   * @param {string} data.category - Categoría del fichero (max 50 chars)
   * @param {string} data.fileName - Nombre del archivo
   * @param {string} data.mimeType - Tipo MIME del archivo
   * @param {number} data.size - Tamaño del archivo en bytes
   * @returns {Promise} - Response con { fileId, uploadUrl, uploadFields }
   */
  const requestUpload = customEndpoint((data) => ({
    url: '/files/request-upload',
    method: 'POST',
    data: {
      promotionId: data.promotionId,
      category: data.category,
      fileName: data.fileName,
      mimeType: data.mimeType,
      size: data.size,
      ...(data.context ? { context: data.context } : {}),
      ...(data.entityType ? { entityType: data.entityType } : {}),
      ...(data.entityId ? { entityId: data.entityId } : {})
    },
    deserialize: false
  }))

  /**
   * POST /files/request-tmp-upload
   * Solicita URL presigned para subida temporal
   * @param {Object} requestData - Datos del archivo a subir
   * @param {string} requestData.fileName - Nombre del archivo
   * @param {string} requestData.mimeType - Tipo MIME del archivo
   * @param {number} requestData.size - Tamaño del archivo en bytes
   * @returns {Promise} - Response con tempPath, uploadUrl y uploadFields
   */
  const requestTmpUpload = customEndpoint((requestData) => ({
    url: '/files/request-tmp-upload',
    method: 'POST',
    data: {
      fileName: requestData.fileName,
      mimeType: requestData.mimeType,
      size: requestData.size
    },
    deserialize: false
  }))

  /**
   * GET /files/:id/download
   * Obtiene URL de descarga (presigned, expira en 1h)
   * @param {string|number} fileId - ID del fichero
   * @returns {Promise} - Response con la URL de descarga
   */
  const getDownloadUrl = customEndpoint((fileId) => ({
    url: `/files/${fileId}/download`,
    method: 'GET',
    deserialize: true
  }))

  /**
   * GET /files/presigned-url
   * Obtiene una presigned URL para leer un archivo del bucket privado
   * @param {string} filePath - Ruta del archivo en el bucket
   * @param {Object} options - Opciones adicionales
   * @param {number} options.expiresIn - Tiempo de expiración en segundos (default: 3600)
   * @returns {Promise} - Response con la presigned URL
   */
  const getPresignedUrl = customEndpoint((filePath, options = {}) => ({
    url: '/files/presigned-url',
    method: 'GET',
    params: {
      path: filePath,
      expiresIn: options.expiresIn || 3600
    },
    deserialize: true
  }))

  return {
    // CRUD methods
    list,
    getById,
    update,
    delete: deleteFile,

    // Upload methods
    requestUpload,
    requestTmpUpload,

    // Download/preview methods
    getDownloadUrl,
    getPresignedUrl
  }
}
